<template>
  <question-base-wrapper
    :question-id="questionId || 'modifications'"
    :title="questionLabel"
    v-bind="baseWrapperProps"
    :is-loading="modifications.loading"
  >
    <!-- MODIFICATIONS ON/OFF -->
    <validation-provider
      v-slot="{ failed }"
      :vid="`${stepId}_vehicle-modified-from-manufacturer`"
      name="Vehicle Modifications"
      rules="required"
    >
      <radio
        v-model="questionData.vehicleModifiedFromManufacturer"
        :data-test="snakeCase(questionFields.vehicleModifiedFromManufacturer)"
        name="vehicle-has-modifications"
        :items="constants.YES_NO"
        :error="failed"
        @change="onModificationStatusChange"
      />
    </validation-provider>
    <!-- MODIFICATIONS LIST -->
    <transition name="fade-in">
      <form-row
        v-if="questionData.vehicleModifiedFromManufacturer"
        id="vehicle_number-of-modifications"
        class="mt-6"
        :question="subtitleValue"
      >
        <validation-provider
          v-slot="{ errors, failed }"
          :vid="`${stepId}_vehicle-modified-from-manufacturer`"
          name="Number of modifications"
          :rules="{ minValue: 1 }"
        >
          <input
            type="hidden"
            :value="modifications.qty"
          >
          <div
            v-if="failed && errors && errors.length"
            class="error--text fz12"
          >
            <p>There should be at least 1 modification selected.</p>
          </div>
        </validation-provider>
        <ul class="list-group">
          <li
            v-for="category in modifications.categories"
            :key="category.value"
            class="category"
            :class="{
              'is-open': category.isOpen,
            }"
          >
            <a
              href="#"
              class="opener"
              :data-test="snakeCase(category.value)"
              @click.prevent="category.isOpen = !category.isOpen"
            >
              <span class="opener-text">{{ category.text }}<template v-if="modsByCategoryQty[category.value]"> ({{ modsByCategoryQty[category.value] }})</template></span>
              <v-icon
                class="opener-icon"
                color="primary"
              >mdi-chevron-down</v-icon>
            </a>
            <transition name="slide-fade">
              <ul
                v-if="category.isOpen"
                class="content list-category-group"
              >
                <li
                  v-for="(text, modification) in modifications.data[category.value]"
                  :key="`${category.value}-${modification}`"
                  class="list-category-group-item capitalized"
                >
                  <checkbox
                    :checked="!!questionData.vehicleModifications[modification]"
                    :data-test="snakeCase(modification)"
                    @change="onModificationChange($event, modification)"
                  >
                    <span v-html="text" />
                  </checkbox>
                </li>
              </ul>
            </transition>
          </li>
        </ul>
      </form-row>
    </transition>
  </question-base-wrapper>
</template>

<script>
import QuestionBase from './Base';
import { startCase } from 'lodash';

export default {
  name: 'question-modifications',
  extends: QuestionBase,
  data () {
    return {
      fields: ['vehicleModifiedFromManufacturer', 'vehicleModifications'],
      modifications: {
        loading: false,
        loaded: false,
        data: null,
        categories: null,
        qty: 0,
      },
      modificationsBackup: {},
    };
  },
  computed: {
    questionLabel () {
      return this.title || `Has the ${this.productName === 'car' ? 'car' : 'vehicle'} been modified in any way?`;
    },
    subtitleValue () {
      return this.subtitle || `Please detail which modifications have been made to the ${this.productName === 'bike' ? 'motorcycle' : this.productName}.`;
    },
    modsByCategoryQty () {
      const result = {};

      this.modifications.categories.forEach(category => {
        const qty = Object.keys(this.modifications.data[category.value]).reduce((acc, value) => {
          if (this.stepData.vehicleModifications[value]) return acc + 1;
          return acc;
        }, 0);
        if (qty === 0) return;
        result[category.value] = qty;
      });
      return result;
    },
  },
  created () {
    const {
      vehicleModifications,
      vehicleModifiedFromManufacturer,
    } = this.questionData;

    if (vehicleModifications && Object.keys(vehicleModifications).length > 0) {
      this.modifications.qty = Object.keys(vehicleModifications).length;
    }

    if (vehicleModifiedFromManufacturer && Object.keys(vehicleModifications).length > 0) {
      this.loadModifications();
    }
  },
  watch: {
    'questionData.vehicleModifiedFromManufacturer': async function (val) {
      if (!val) {
        this.questionData.vehicleModifications = {};
      } else if (!this.modifications.loaded) {
        await this.loadModifications();
      }
    },
    'questionData.vehicleModifications': {
      handler () {
        this.modifications.qty = Object.keys(this.stepData.vehicleModifications).length;
      },
      deep: true,
    },
  },
  methods: {
    async onModificationStatusChange (value) {
      if (value === false) {
        this.modificationsBackup = this.stepData.vehicleModifications;
        this.questionData.vehicleModifications = {};
      } else {
        this.questionData.vehicleModifications = this.modificationsBackup;
        this.modificationsBackup = {};
      }
    },
    onModificationChange (event, modificationValue) {
      const status = this.stepData.vehicleModifications[modificationValue];
      if (status) {
        this.$delete(this.questionData.vehicleModifications, modificationValue);
      } else {
        this.$set(this.questionData.vehicleModifications, modificationValue, 1);
      }
    },
    async loadModifications () {
      this.modifications.loading = true;
      try {
        const data = await this.$api.getVehicleModifications(this.constants.VEHICLE_SYSTEM_TYPES[this.productName]);
        this.modifications.data = data.data;
        this.modifications.categories = Object.keys(data.data).map(key => {
          const category = {
            text: startCase(key).split(' ').join(' / '),
            value: key,
            isOpen: false,
          };

          if (key === 'bodyModifications') {
            category.text = 'Body modifications';
          }
          return category;
        });
        this.modifications.loaded = true;
      } catch (error) {
        console.log(error.toString());
      }
      this.modifications.loading = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.list-group {
  padding: 0;

  .category {
    border-bottom: 1px solid $clr-outline;
  }

  .content {
    will-change: transform, opacity;
  }

  .opener {
    display: flex;
    color: currentColor;
    text-decoration: none;
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    padding: 18px 20px 18px 12px;

    & + * { margin-top: 16px; }

    .opener-icon { margin-left: auto; }

    .opener-text {
      text-decoration: underline;
      margin-right: auto;
    }

    &:hover {
      background-color: rgba(var(--v-primary-rgb, $clr-primary-rgb), 0.05);

      span { text-decoration: none; }
    }
  }

  .category.is-open .opener-icon {
    transform: rotate(180deg);
  }
}

.list-category-group {
  display: flex;
  flex-wrap: wrap;

  .list-category-group-item {
    margin: 0 16px 16px 0;
  }
}

.capitalized {
  &::v-deep {
    .checkbox__title {
      text-transform: lowercase;

      &::first-letter {
        text-transform: capitalize;
      }
    }
  }
}

</style>
